import TagManager from 'react-gtm-module'

import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { ID } from 'src/Types/common'

import { EventNameEnum, EventTypeEnum } from '../tagManagerEnums'

export const creatorReviewAvailableSongs = ({
  userID,
  reviewId,
  campaignId,
  trackId,
}: {
  userID: number | string
  reviewId: number | string
  campaignId?: number | string
  trackId?: number | string
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      userID,
      event: EventTypeEnum.CreatorReviewAvailableSongs,
      event_name: EventNameEnum.CreatorReviewAvailableSongs,
      'account-type': 'tiktok_creator',
      'campaign-platform': CampaignPlatformTypeEnum.TIKTOK,
      'review-id': reviewId,
      'campaign-id': campaignId,
      'track-id': trackId,
    },
  })
}

export const creatorAcceptedTrack = ({
  userID,
  reviewId,
  campaignId,
  trackId,
  amountPaid,
}: {
  userID: number | string
  reviewId: number | string
  campaignId?: number | string
  trackId?: number | string
  amountPaid?: number | string
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      userID,
      event: EventTypeEnum.CreatorAcceptedTrack,
      event_name: EventNameEnum.CreatorAcceptedTrack,
      'account-type': 'tiktok_creator',
      'campaign-platform': CampaignPlatformTypeEnum.TIKTOK,
      'review-id': reviewId,
      'campaign-id': campaignId,
      'track-id': trackId,
      amount_paid: amountPaid,
    },
  })
}

export const creatorCancelledTask = ({
  userID,
  reviewId,
  campaignId,
  trackId,
  amountPaid,
}: {
  userID: number | string
  reviewId: number | string
  campaignId?: number | string
  trackId?: number | string
  amountPaid?: number | string
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      userID,
      event: EventTypeEnum.CreatorCancelledTask,
      event_name: EventNameEnum.CreatorCancelledTask,
      'account-type': 'tiktok_creator',
      'campaign-platform': CampaignPlatformTypeEnum.TIKTOK,
      'review-id': reviewId,
      'campaign-id': campaignId,
      'track-id': trackId,
      amount_paid: amountPaid,
    },
  })
}

export const creatorUploadedVideo = ({
  userID,
  reviewId,
  campaignId,
  trackId,
  amountPaid,
}: {
  userID: number | string
  reviewId: number | string
  campaignId?: number | string
  trackId?: number | string
  amountPaid?: number | string
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      userID,
      event: EventTypeEnum.CreatorUploadedVideo,
      event_name: EventNameEnum.CreatorUploadedVideo,
      'account-type': 'tiktok_creator',
      'campaign-platform': CampaignPlatformTypeEnum.TIKTOK,
      'review-id': reviewId,
      'campaign-id': campaignId,
      'track-id': trackId,
      amount_paid: amountPaid,
    },
  })
}

export const creatorPayoutRequested = ({
  userID,
  payoutAmount,
  payoutRequestId,
}: {
  userID: number | string
  payoutAmount?: number | string
  payoutRequestId?: number | string
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventNameEnum.PayoutRequested,
      event_name: EventNameEnum.PayoutRequested,
      'account-type': 'creator',
      userID,
      ...(payoutAmount ? { payout_amount: payoutAmount } : {}),
      ...(payoutRequestId ? { payout_request_id: payoutRequestId } : {}),
    },
  })
}

export enum EligibilityEnum {
  ELIGIBLE = 'eligible',
  NOT_ELIGIBLE = 'not_eligible',
  AWAITING_ELIGIBILITY = 'awaiting',
}

export const creatorVisitSoundsInfoEvent = ({
  userID,
  soundsCount,
  eligibility,
  isBlocked,
}: {
  userID?: ID | null
  soundsCount?: number
  eligibility?: EligibilityEnum
  isBlocked?: boolean
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.GA_EVENT,
      event_params: {
        event_name: EventNameEnum.CreatorVisitSoundsInfo,
        'campaign-platform': CampaignPlatformTypeEnum.TIKTOK,
        sounds_count: soundsCount,
        eligible_status: eligibility,
        is_blocked: isBlocked,
      },
      user_properties: {
        ...(userID ? { userID } : {}),
        account_type: 'creator',
      },
    },
  })
}

export const soundPopupOpenedEvent = ({
  userID,
  available,
  reviewId,
}: {
  userID?: ID | null
  available?: boolean
  reviewId?: number | string
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.GA_EVENT,
      event_params: {
        event_name: EventNameEnum.SoundPopupOpened,
        'campaign-platform': CampaignPlatformTypeEnum.TIKTOK,
        is_sound_available: available,
        'campaign-id': reviewId,
      },
      user_properties: {
        ...(userID ? { userID } : {}),
        account_type: 'creator',
      },
    },
  })
}

export const playerPressedEvent = ({
  reviewId,
  userID,
}: {
  reviewId: number | string
  userID?: ID | null
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.GA_EVENT,
      event_params: {
        event_name: EventNameEnum.PlayerPressed,
        'campaign-platform': CampaignPlatformTypeEnum.TIKTOK,
        'campaign-id': reviewId,
      },
      user_properties: {
        ...(userID ? { userID } : {}),
        account_type: 'creator',
      },
    },
  })
}

export const creatorRegStartEvent = (): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.GA_EVENT,
      event_params: {
        event_name: EventNameEnum.CreatorRegStart,
        'campaign-platform': CampaignPlatformTypeEnum.TIKTOK,
      },
      user_properties: {
        account_type: 'creator',
      },
    },
  })
}

export const creatorRegAuthorizationStartedEvent = (): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.GA_EVENT,
      event_params: {
        event_name: EventNameEnum.CreatorRegAuthorizationStarted,
        'campaign-platform': CampaignPlatformTypeEnum.TIKTOK,
      },
      user_properties: {
        account_type: 'creator',
      },
    },
  })
}

export const creatorRegAuthorizationFailedEvent = (): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.GA_EVENT,
      event_params: {
        event_name: EventNameEnum.CreatorRegAuthorizationFailed,
        'campaign-platform': CampaignPlatformTypeEnum.TIKTOK,
      },
      user_properties: {
        account_type: 'creator',
      },
    },
  })
}

export const creatorRegAuthorizationSuccessEvent = (): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.GA_EVENT,
      event_params: {
        event_name: EventNameEnum.CreatorRegAuthorizationSuccess,
        'campaign-platform': CampaignPlatformTypeEnum.TIKTOK,
      },
      user_properties: {
        account_type: 'creator',
      },
    },
  })
}

export const creatorRegAuthorizationProceedEvent = (): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.GA_EVENT,
      event_params: {
        event_name: EventNameEnum.CreatorRegAuthorizationProceed,
        'campaign-platform': CampaignPlatformTypeEnum.TIKTOK,
      },
      user_properties: {
        account_type: 'creator',
      },
    },
  })
}

export const creatorRegEmailConfirmationRequestedEvent = (): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.GA_EVENT,
      event_params: {
        event_name: EventNameEnum.CreatorRegEmailConfirmationRequested,
        'campaign-platform': CampaignPlatformTypeEnum.TIKTOK,
      },
      user_properties: {
        account_type: 'creator',
      },
    },
  })
}

export const creatorSavedMinRewardEvent = ({
  userID,
  minDesiredReward,
}: {
  userID: ID | null
  minDesiredReward: number
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.GA_EVENT,
      event_params: {
        event_name: EventNameEnum.CreatorSavedMinReward,
        'campaign-platform': CampaignPlatformTypeEnum.TIKTOK,
        min_desired_reward: minDesiredReward,
      },
      user_properties: {
        ...(userID ? { userID } : {}),
        account_type: 'creator',
      },
    },
  })
}
